import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Legal.css';

const Terms: React.FC = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="legal-container">
      <div className="legal-card">
        <div className="legal-header">
          {!localStorage.getItem('token') ? <h1>MINDCAST</h1> : null}
          {!location.pathname.startsWith('/legal') && (
            <div className="legal-nav-dropdown" ref={dropdownRef}>
              <button className="legal-nav-button" onClick={() => setShowDropdown(!showDropdown)}>
                <span className="legal-nav-text">Terms of Use</span>
                <div className={`legal-nav-icon ${showDropdown ? 'open' : ''}`}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </div>
              </button>

              {showDropdown && (
                <div className="legal-nav-menu">
                  <button className="legal-nav-item active" onClick={() => window.location.href = '/terms'}>
                    <span>Terms of Use</span>
                    <svg className="legal-nav-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  </button>
                  <button className="legal-nav-item" onClick={() => window.location.href = '/privacy'}>
                    <span>Privacy Policy</span>
                  </button>
                  <button className="legal-nav-item" onClick={() => window.location.href = '/community-guidelines'}>
                    <span>Community Guidelines</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {!localStorage.getItem('token') ? (
            <Link to="/register" className="back-link">←</Link>
          ) : null}
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <p>
              Welcome to Mindcast, the playground for curious minds. These Terms of Use ("Terms") govern your access to and use of Mindcast (the "Service"). By creating an account or using Mindcast, you agree to these Terms. If you do not agree, please do not use the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>1. The Mindcast Service</h2>
            <p>Mindcast is a platform to explore, share, and discuss books, podcasts, and ideas. The Service includes:</p>
            <ul>
              <li>A community where users can post, comment, and interact.</li>
              <li>Access to media via licensed integrations and APIs.</li>
              <li>Features to help you discover and share ideas safely.</li>
            </ul>
            <p>We are constantly improving the Service and may update, add, or remove features at any time.</p>
          </section>

          <section className="legal-section">
            <h2>2. Who Can Use Mindcast</h2>
            <p>To use Mindcast, you must:</p>
            <ul>
              <li>Be at least 16 years old (or meet the minimum age in your jurisdiction).</li>
              <li>Not be legally prohibited from using online services.</li>
              <li>Provide accurate and up-to-date information when registering.</li>
              <li>Not have a previous account permanently disabled for violating our Terms.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Prohibited Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Post content that is unlawful, harmful, misleading, abusive, hateful, violent, or infringes on others' rights.</li>
              <li>Share private information without consent or violate intellectual property rights.</li>
              <li>Impersonate others or misrepresent yourself.</li>
              <li>Spam, harass, or abuse other users.</li>
              <li>Use bots, scrapers, or hacks to collect data or interfere with the Service.</li>
              <li>Sell, license, or trade accounts or access credentials.</li>
              <li>Attempt to reverse engineer, copy, or replicate the Service, its UX/UI, or its core functionality.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Your Content & Our Rights</h2>
            <div className="subsection">
              <h3>Ownership</h3>
              <p>You retain ownership of what you post. By sharing content on Mindcast, you grant us a non-exclusive, royalty-free, worldwide license to host, display, distribute, and otherwise use your content in connection with the Service. This license ends when you delete your content or account.</p>
            </div>
            <div className="subsection">
              <h3>User Opinions</h3>
              <p>All content posted by users represents their own opinions, thoughts, and experiences, not those of Mindcast. Mindcast does not endorse, verify, or assume responsibility for user content.</p>
            </div>
            <div className="subsection">
              <h3>Third-Party Content & APIs</h3>
              <p>Mindcast may provide access to content via APIs or third-party services (e.g., books, podcasts). We do not control this content and are not responsible for its accuracy, availability, or any interactions you have with third-party services.</p>
            </div>
            <div className="subsection">
              <h3>Content Removal</h3>
              <p>We may remove or restrict content, or suspend accounts, if content violates these Terms, community standards, or applicable law.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2>5. Privacy</h2>
            <p>We collect and use information to provide and improve the Service. We do not sell or share personal data with third parties for advertising purposes. For more details, see our Privacy Policy.</p>
          </section>

          <section className="legal-section">
            <h2>6. Our Brand, Intellectual Property & Service Rights</h2>
            <ul>
              <li>Mindcast retains all rights, title, and interest in and to our brand, logos, software, designs, user interface, user experience, content, and other intellectual property, whether registered or not.</li>
              <li>You may not use, reproduce, distribute, modify, or create derivative works of any Mindcast intellectual property without our prior written consent.</li>
              <li>Usernames or other identifiers that infringe on trademarks, mislead others, or are abusive may be changed, reclaimed, or removed by Mindcast at our discretion.</li>
              <li>We may enforce all legal protections over our intellectual property and Service at our discretion, including against scraping, unauthorized use of APIs, reverse engineering, or replication of UX/UI.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Account Suspension & Termination</h2>
            <p>You can delete your account at any time. We may, at our sole discretion and without prior notice, suspend or terminate your account if we believe you have violated these Terms, engaged in conduct that creates legal, security, or reputational risk, harmed or could harm the community, or otherwise acted in a manner we deem inappropriate.</p>
            <p>Mindcast is not responsible for any losses or damages resulting from suspension or termination, and such actions do not require prior warning or liability on our part.</p>
            <p>Full deletion of your data may take up to 90 days, including from backups.</p>
          </section>

          <section className="legal-section">
            <h2>8. Disclaimer & Liability</h2>
            <p>Mindcast is provided "as is." We do not guarantee the Service will be error-free, uninterrupted, or completely secure.</p>
            <p>To the fullest extent permitted by law, we are not responsible for:</p>
            <ul>
              <li>User content or actions.</li>
              <li>Any indirect, incidental, or consequential damages, including lost data or profits.</li>
            </ul>
            <p>You use the Service at your own risk.</p>
          </section>

          <section className="legal-section">
            <h2>9. Governing Law & Disputes</h2>
            <p>These Terms are governed by the laws of the Province of Quebec, Canada, without regard to conflict-of-law rules. Any disputes arising under these Terms will be resolved exclusively in the courts located in Quebec, Canada. Users accessing Mindcast from other jurisdictions agree to comply with their local laws, but Mindcast makes no representation regarding enforceability of these Terms outside Quebec.</p>
          </section>

          <section className="legal-section">
            <h2>10. Updates to These Terms</h2>
            <p>We may update these Terms from time to time. Significant changes will be communicated via the app or email. Continuing to use Mindcast after changes indicates your acceptance of the updated Terms.</p>
          </section>

          <section className="legal-section">
            <h2>11. Feedback</h2>
            <p>We welcome your ideas! By sharing feedback, you agree that Mindcast may use it freely without obligation to compensate you.</p>
          </section>
        </div>

        <div className="legal-footer">
          <p>By using Mindcast, you acknowledge that you have read and understood these terms and conditions.</p>
          <p className="legal-subtitle">Last Updated: September 27th, 2025</p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
